import { KeyRound, Moon, Sun } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useCreditBalance } from '../../hooks/use-credits'
import { cn } from '../../lib/utils'
import { useAppStore } from '../../stores/app-store'

/** Workspace settings, billing reachability, credentials, and display preference. */
export function WorkspaceSettingsTab({ isDark }: { isDark: boolean }) {
  const navigate = useNavigate()
  const toggleTheme = useAppStore((state) => state.toggleTheme)
  const { data: balance, isLoading } = useCreditBalance()
  const total = balance?.totalCredits ?? 0
  const themeAction = isDark ? 'Use light theme' : 'Use dark theme'

  return (
    <div className="space-y-4">
      <h2 className={cn('text-sm font-semibold', isDark ? 'text-neutral-200' : 'text-neutral-800')}>
        Workspace
      </h2>
      <div
        className={cn(
          'rounded-lg border p-4 space-y-3',
          isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-white',
        )}
      >
        <div>
          <span
            className={cn('text-xs font-medium', isDark ? 'text-neutral-400' : 'text-neutral-500')}
          >
            Workspace name
          </span>
          <p className={cn('text-sm mt-0.5', isDark ? 'text-neutral-200' : 'text-neutral-800')}>
            AgentWiki
          </p>
        </div>
        <div className="flex items-end justify-between gap-3">
          <div>
            <span
              className={cn(
                'text-xs font-medium',
                isDark ? 'text-neutral-400' : 'text-neutral-500',
              )}
            >
              Credit balance
            </span>
            <p className={cn('text-sm mt-0.5', isDark ? 'text-neutral-200' : 'text-neutral-800')}>
              {isLoading ? '—' : `${total.toLocaleString()} credits`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/billing')}
            className="cursor-pointer rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700"
          >
            Manage billing
          </button>
        </div>
      </div>

      <div
        className={cn(
          'rounded-lg border p-4 space-y-4',
          isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-white',
        )}
      >
        <div>
          <h3
            className={cn('text-sm font-medium', isDark ? 'text-neutral-200' : 'text-neutral-800')}
          >
            Workspace tools
          </h3>
          <p className="mt-1 text-xs text-neutral-500">
            Manage workspace credentials and your display preference from their explicit settings
            destinations.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/secrets"
            className={cn(
              'inline-flex min-h-11 items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium',
              isDark
                ? 'border-white/[0.1] text-neutral-300 hover:bg-surface-3'
                : 'border-neutral-200 text-neutral-700 hover:bg-neutral-50',
            )}
          >
            <KeyRound className="h-4 w-4" aria-hidden="true" />
            Manage secrets
          </Link>
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={themeAction}
            className={cn(
              'inline-flex min-h-11 items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium',
              isDark
                ? 'border-white/[0.1] text-neutral-300 hover:bg-surface-3'
                : 'border-neutral-200 text-neutral-700 hover:bg-neutral-50',
            )}
          >
            {isDark ? (
              <Sun className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Moon className="h-4 w-4" aria-hidden="true" />
            )}
            {themeAction}
          </button>
        </div>
      </div>
    </div>
  )
}
